/**
 * Google Geocoder Utility
 *
 * Pure functions for interacting with Google Maps Geocoding API.
 * Handles forward geocoding, reverse geocoding, and address component parsing.
 */

import Logger from '../lib/logger'

const GEOCODE_BASE_URL = 'https://maps.googleapis.com/maps/api/geocode/json'

/**
 * Types mapped to address field targets for parsing geocoding results
 */
const COMPONENT_FIELD_MAP = {
  route: 'street',
  street_number: 'streetnumber',
  administrative_area_level_2: 'city',
  administrative_area_level_1: 'uf',
  postal_code: 'zipcode'
}

/**
 * Check if an address component represents a neighborhood (sublocality)
 */
export function isNeighborhoodComponent(component) {
  const types = component.types || []
  return types.includes('political') &&
    types.includes('sublocality') &&
    types.includes('sublocality_level_1')
}

/**
 * Parse Google Geocoding address components into an address fields object
 */
export function parseAddressComponents(components) {
  const result = {}

  if (!components || !Array.isArray(components)) return result

  for (const component of components) {
    const primaryType = component.types?.[0]

    if (primaryType && COMPONENT_FIELD_MAP[primaryType]) {
      const field = COMPONENT_FIELD_MAP[primaryType]
      // Use short_name for street_number and state (uf)
      // Brazilian states (administrative_area_level_1) should be abbreviations (UF)
      if (primaryType === 'street_number' || primaryType === 'administrative_area_level_1') {
        result[field] = component.short_name
      } else {
        result[field] = component.long_name
      }
    }

    if (isNeighborhoodComponent(component)) {
      result.neighborhood = component.long_name
    }
  }

  return result
}

/**
 * Build address string from individual parts
 */
export function buildAddressString(street, streetnumber, neighborhood, city, uf) {
  const parts = [street, streetnumber, neighborhood, city, uf]
    .filter(part => part && part.trim())

  return parts.join(', ')
}

/**
 * Geocode an address string using Google Maps API
 *
 * @param {string} apiKey - Google Maps API key
 * @param {string} address - Full address string
 * @returns {Promise<Object|null>} Resolves with { coordinates: { lat, lng }, formattedAddress, addressComponents } or null
 */
export async function geocodeAddress(apiKey, address) {
  if (!apiKey || !address) return null

  const url = `${GEOCODE_BASE_URL}?address=${encodeURIComponent(address)}&key=${apiKey}`

  const response = await fetch(url)
  const data = await response.json()

  if (data.results && data.results.length > 0) {
    const result = data.results[0]
    return {
      coordinates: {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng
      },
      formattedAddress: result.formatted_address,
      addressComponents: result.address_components
    }
  }

  return null
}

/**
 * Reverse geocode coordinates to address using Google Maps API
 *
 * @param {string} apiKey - Google Maps API key
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude (passed as `lng` key but used as second param)
 * @returns {Promise<Object|null>} Resolves with { formattedAddress, addressComponents } or null
 */
export async function reverseGeocode(apiKey, lat, lng) {
  if (!apiKey) return null

  const url = `${GEOCODE_BASE_URL}?latlng=${lat},${lng}&key=${apiKey}`

  const response = await fetch(url)
  const data = await response.json()

  if (data.results && data.results.length > 0) {
    const result = data.results[0]
    return {
      formattedAddress: result.formatted_address,
      addressComponents: result.address_components
    }
  }

  return null
}

// TODO: consider extracting the geocoder in a separate module if needed for other controllers later
/**
 * Fetch address from CEP (Brazilian ZIP code) via API endpoint
 */
export async function fetchCEP(cep) {
  if (!cep) return null

  const response = await fetch(`/api/addresses/cep?zip_code=${encodeURIComponent(cep)}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })

  if (!response.ok) {
    throw new Error(`CEP lookup failed: ${response.status}`)
  }

  return await response.json()
}

/**
 * Apply CEP mask: XXXXX-XXX format (max 9 digits)
 */
export function applyCEPMask(value) {
  if (!value || typeof value !== 'string') return value

  let digits = value.replace(/\D/g, '')
  digits = digits.substring(0, 9)

  if (digits.length <= 5) {
    return digits
  } else {
    return `${digits.substring(0, 5)}-${digits.substring(5)}`
  }
}
